import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import WordItem from '../components/Words/WordItem';
import AddWordModal from '../components/Words/AddWordModal';
import GenerateWordsModal from '../components/Words/GenerateWordsModal';
import ManageTagsModal from '../components/Tags/ManageTagsModal';
import TagWordsModal from '../components/Tags/TagWordsModal';
import MoveWordsModal from '../components/Words/MoveWordsModal';
import { api } from '../config/api';

const WordListDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [words, setWords] = useState([]);
  const [selectedWords, setSelectedWords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isManageTagsModalOpen, setIsManageTagsModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isUntagModalOpen, setIsUntagModalOpen] = useState(false);
  const [tagModalMode, setTagModalMode] = useState('add');
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  
  useEffect(() => {
    fetchList();
    fetchWords();
  }, [id]);

  const reloadList = async () => {
    await fetchList();
    await fetchWords();
  }

  const fetchList = async () => {
    try {
      const data = await api.getWordList(id);
      setList(data);
    } catch (err) {
      setError(err.message || 'Wystąpił błąd podczas pobierania listy');
    }
  };

  const fetchWords = async () => {
    setIsLoading(true);
    try {
      const data = await api.getWords(id);
      setWords(data);
    } catch (err) {
      setError(err.message || 'Wystąpił błąd podczas pobierania słówek');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddWordSuccess = (newWord) => {
    setWords(prevWords => [...prevWords, newWord]);
  };

  const handleGenerateWordsSuccess = async (selectedWords) => {
    try {
      const newWords = await Promise.all(
        selectedWords.map(word => 
          api.createWord(id, {
            original: word.word,
            translation: word.translation,
            notes: ''
          })
        )
      );
      setWords(prevWords => [...prevWords, ...newWords]);
    } catch (err) {
      setError(err.message || 'Wystąpił błąd podczas zapisywania wygenerowanych słówek');
    }
  };

  const handleDeleteWord = async (wordId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to słówko?')) {
      return;
    }

    try {
      await api.deleteWord(wordId);
      setWords(prevWords => prevWords.filter(word => word.id !== wordId));
    } catch (err) {
      setError(err.message || 'Wystąpił błąd podczas usuwania słówka');
    }
  };

  const handleUpdateWord = (updatedWord) => {
    setWords(prevWords => prevWords.map(word => 
      word.id === updatedWord.id ? updatedWord : word
    ));
  };

  const handleDeleteList = async () => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę listę? Wszystkie słówka zostaną usunięte.')) {
      return;
    }

    try {
      await api.deleteWordList(id);
      navigate('/lists');
    } catch (err) {
      setError(err.message || 'Wystąpił błąd podczas usuwania listy');
    }
  };

  const handleToggleWordSelection = (wordId) => {
    setSelectedWords(prevSelected => {
      if (prevSelected.includes(wordId)) {
        return prevSelected.filter(id => id !== wordId);
      } else {
        return [...prevSelected, wordId];
      }
    });
  };

  const handleOpenTagModal = (mode) => {
    if (selectedWords.length === 0) {
      setError('Najpierw zaznacz słówka, które chcesz otagować');
      return;
    }
    
    setTagModalMode(mode);
    if (mode === 'add') {
      setIsTagModalOpen(true);
    } else {
      setIsUntagModalOpen(true);
    }
  };

  const handleCloseTagModal = (success = false) => {
    setIsTagModalOpen(false);
    setIsUntagModalOpen(false);
    
    if (success) {
      // Odświeżamy listę słów, aby zobaczyć zaktualizowane tagi
      fetchWords();
    }
  };

  const handleTagDeleted = (tagId) => {
    // Odśwież listę słów, aby usunąć usunięty tag
    setWords(words.map(word => ({
      ...word,
      tags: word.tags.filter(tag => tag.id !== tagId)
    })));
  };

  const handleCloseMoveModal = (success = false) => {
    setIsMoveModalOpen(false);
    if (success) {
      fetchWords();
      setSelectedWords([]);
    }
  };

  if (!list) {
    return (
      <div className="flex justify-center items-center h-60">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="relative pb-16">
      <div className="mb-6">
        <nav className="flex items-center space-x-2 text-sm mb-4">
          <Link to="/lists" className="text-neutral-500 hover:text-neutral-700 transition-colors">
            Listy słówek
          </Link>
          <span className="text-neutral-300">/</span>
          <span className="text-neutral-700 font-medium">{list.name}</span>
        </nav>

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h1 className="text-xl font-semibold mb-1">{list.name}</h1>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-sm text-neutral-500">{list.language_name}</span>
              <span className="text-sm text-neutral-400">•</span>
              <span className="text-sm text-neutral-500">słówek: {list.words_count || 0}</span>
              {words.length > 0 && (
                <>
                  <span className="text-sm text-neutral-400">•</span>
                  <span className="text-sm text-neutral-500">
                    zaznaczono: {selectedWords.length}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto float-end self-end">
            <button
              onClick={() => setIsManageTagsModalOpen(true)}
              className="w-1/3 md:w-auto px-3 py-1 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
            >
              Tagi
            </button>
            <button
              onClick={() => setIsGenerateModalOpen(true)}
              className="w-1/3 md:w-auto px-3 py-1 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
            >
              Generuj
            </button>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="w-1/3 md:w-auto px-3 py-1 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
            >
              Dodaj
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 text-sm bg-red-50 text-red-600 rounded-md">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-60">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
        </div>
      ) : words.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-60 bg-white p-6 rounded-lg border border-neutral-200">
          <svg className="w-16 h-16 text-neutral-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
          </svg>
          <p className="text-neutral-500 text-sm mb-4">Nie masz jeszcze żadnych słówek w tej liście</p>
          <div className="flex gap-2">
            <button
              onClick={() => setIsGenerateModalOpen(true)}
              className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
            >
              Generuj słówka
            </button>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
            >
              Dodaj słówko
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {words.map(word => (
            <WordItem
              key={word.id}
              word={word}
              onDelete={handleDeleteWord}
              onUpdate={handleUpdateWord}
              isSelected={selectedWords.includes(word.id)}
              onToggleSelection={() => handleToggleWordSelection(word.id)}
            />
          ))}
        </div>
      )}

      {selectedWords.length > 0 && (
        <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-neutral-200 py-3 flex justify-center gap-4 shadow-md z-10 mt-6">
          <button
            onClick={() => handleOpenTagModal('add')}
            className="px-4 py-2 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
          >
            Otaguj
          </button>
          <button
            onClick={() => handleOpenTagModal('remove')}
            className="px-4 py-2 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
          >
            Odtaguj
          </button>
          <button
            onClick={() => setIsMoveModalOpen(true)}
            className="px-4 py-2 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
          >
            Przenieś
          </button>
        </div>
      )}

      <AddWordModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddWordSuccess}
        listId={id}
      />

      <GenerateWordsModal
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        onSave={handleGenerateWordsSuccess}
        listId={id}
        language={list.language_name}
      />

      <ManageTagsModal
        isOpen={isManageTagsModalOpen}
        onClose={() => setIsManageTagsModalOpen(false)}
        listId={id}
        onTagDeleted={handleTagDeleted}
      />

      <TagWordsModal
        isOpen={isTagModalOpen}
        onClose={handleCloseTagModal}
        listId={id}
        wordIds={selectedWords}
        mode="add"
      />

      <TagWordsModal
        isOpen={isUntagModalOpen}
        onClose={handleCloseTagModal}
        listId={id}
        wordIds={selectedWords}
        mode="remove"
      />

      <MoveWordsModal
        isOpen={isMoveModalOpen}
        onClose={handleCloseMoveModal}
        wordIds={selectedWords}
        currentListId={id}
        onReload={reloadList}
      />
    </div>
  );
};

export default WordListDetailPage;